import React from 'react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell,
} from 'recharts';

const STATUS_COLORS = {
  participating: '#3b82f6',
  submitted: '#8b5cf6',
  won: '#10b981',
  lost: '#ef4444',
  dropped: '#6b7280',
  canceled: '#f59e0b',
};

function formatINR(v) {
  if (v == null) return '–';
  if (v >= 10000000) return `₹${(v / 10000000).toFixed(1)}Cr`;
  if (v >= 100000) return `₹${(v / 100000).toFixed(1)}L`;
  if (v >= 1000) return `₹${(v / 1000).toFixed(0)}K`;
  return `₹${Number(v).toLocaleString('en-IN')}`;
}

function BidValueStatsRow({ stats, loading }) {
  const items = [
    { label: 'Submitted Value', value: formatINR(stats?.total_submitted_value), color: '#8b5cf6' },
    { label: 'Won Value', value: formatINR(stats?.total_won_value), color: '#10b981' },
    { label: 'Win Rate', value: stats?.win_rate != null ? `${stats.win_rate}%` : '–', color: '#b60000' },
  ];
  return (
    <div className="stats-grid" style={{ marginBottom: 24 }}>
      {items.map((s, i) => (
        <div key={i} className="stat-card">
          <div className="stat-content">
            <span className="stat-label">{s.label}</span>
            <span className="stat-value">{loading ? '…' : s.value}</span>
            <span className="stat-change" style={{ color: s.color }}>Bid tracking</span>
          </div>
        </div>
      ))}
    </div>
  );
}

function PipelineValueChart({ data }) {
  if (!data || data.length === 0) return null;
  return (
    <div className="chart-card">
      <h3>Pipeline Value by Status</h3>
      <div style={{ height: 280 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" tick={{ fontSize: 12 }} />
            <YAxis tickFormatter={formatINR} tick={{ fontSize: 12 }} />
            <Tooltip formatter={(v) => formatINR(v)} />
            <Bar dataKey="value" name="Value" radius={[4, 4, 0, 0]}>
              {data.map((entry, i) => (
                <Cell key={i} fill={STATUS_COLORS[entry.status] || '#6b7280'} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function WinRateBySourceChart({ data }) {
  if (!data || data.length === 0) return null;
  return (
    <div className="chart-card">
      <h3>Win Rate by Source</h3>
      <div style={{ height: 280 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} layout="vertical" margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis type="number" domain={[0, 100]} tickFormatter={(v) => `${v}%`} tick={{ fontSize: 12 }} />
            <YAxis type="category" dataKey="name" width={120} tick={{ fontSize: 12 }} />
            <Tooltip formatter={(v, name) => name === 'win_rate' ? `${v}%` : v} />
            <Bar dataKey="win_rate" name="Win Rate %" fill="#10b981" radius={[0, 4, 4, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function WinRateByBuyerChart({ data }) {
  if (!data || data.length === 0) return null;
  return (
    <div className="chart-card">
      <h3>Win Rate by Client (Top 10)</h3>
      <div style={{ height: Math.max(280, data.length * 40) }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} layout="vertical" margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis type="number" domain={[0, 100]} tickFormatter={(v) => `${v}%`} tick={{ fontSize: 12 }} />
            <YAxis type="category" dataKey="name" width={150} tick={{ fontSize: 11 }} />
            <Tooltip formatter={(v, name) => {
              if (name === 'win_rate') return `${v}%`;
              if (name === 'won_value' || name === 'lost_value') return formatINR(v);
              return v;
            }} />
            <Bar dataKey="win_rate" name="Win Rate %" fill="#3b82f6" radius={[0, 4, 4, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export { BidValueStatsRow, PipelineValueChart, WinRateBySourceChart, WinRateByBuyerChart };
